import React, { useMemo, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import InlineMessage from '@atlaskit/inline-message';
import { Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { getConfigureCustomFieldUrl } from '@atlassian/jira-custom-fields-common/src/common/constants.tsx';
import { fireCmdbErrorAnalytics } from '@atlassian/jira-servicedesk-cmdb-error-handling/src/fireCmdbErrorAnalytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { getJsmChangeEditionUrl } from '@atlassian/jira-servicedesk-common/src/navigation/settings/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import messages from './messages.tsx';

export type Props = {
	isAdmin: boolean;
	isObjectListUnenriched: boolean;
	isCmdbEnabled: boolean;
	hasInvalidWorkspaceId: boolean;
	hasPremium: boolean;
	hasSchema: boolean;
	fieldId: string;
};

const UpgradeToPremiumLink = (chunks: ReactNode) => (
	<a href={getJsmChangeEditionUrl()} target="_blank">
		{chunks}
	</a>
);

export const InlineMessageContent = ({ children }: { children: React.ReactNode }) => (
	<InlineMessageContentWrapper>
		<IconWrapper>
			<ErrorIcon spacing="spacious" label="error" color={token('color.icon.danger')} />
		</IconWrapper>
		<div>{children}</div>
	</InlineMessageContentWrapper>
);

export const NotPremiumMessage = () => {
	const { formatMessage } = useIntl();
	return (
		<Heading size="xsmall" as="span">
			{formatMessage(messages.errorInvalidFieldConfigDowngradedCmdbAssets)}
		</Heading>
	);
};

export const NotEnabledMessage = () => {
	const { formatMessage } = useIntl();
	return (
		<Heading size="xsmall" as="span">
			{formatMessage(messages.errorInvalidFieldConfigDisabledCmdbAssets)}
		</Heading>
	);
};

export const NotPremiumAdminMessage = () => {
	const { formatMessage } = useIntl();
	const notPremiumErrorAdmin = formatMessage(
		messages.errorInvalidFieldConfigDowngradedAdminCmdbAssets,
		{
			a: UpgradeToPremiumLink,
		},
	);
	return (
		<Heading size="xsmall" as="span">
			{notPremiumErrorAdmin}
		</Heading>
	);
};

export const InvalidSchemaMessage = () => {
	const { formatMessage } = useIntl();
	const textContent = <>{formatMessage(messages.errorInvalidFieldConfig)}</>;
	// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
	return isVisualRefreshEnabled() ? <Text as="p">{textContent}</Text> : <p>{textContent}</p>;
};

export const InvalidSchemaAdminMessage = ({ fieldId }: { fieldId: string }) => {
	const { formatMessage } = useIntl();
	const textContent = <>{formatMessage(messages.errorInvalidFieldConfigAdmin)}</>;
	return (
		<>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			{isVisualRefreshEnabled() ? <Text as="p">{textContent}</Text> : <p>{textContent}</p>}
			<Button
				testId="issue-field-cmdb-object.ui.error.button"
				appearance="link"
				href={getConfigureCustomFieldUrl(fieldId)}
				target="_blank"
				interactionName="auto-generated.issue-field-cmdb-object.ui.error.button.button"
			>
				{formatMessage(messages.errorInvalidFieldConfigAdminLink)}
			</Button>
		</>
	);
};

export const ObjectListUnenriched = () => {
	const { formatMessage } = useIntl();
	const textContent = <>{formatMessage(messages.errorFetchingObjectsCmdbAssets)}</>;
	// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
	return isVisualRefreshEnabled() ? <Text as="p">{textContent}</Text> : <p>{textContent}</p>;
};

export const FallbackError = () => {
	const { formatMessage } = useIntl();
	const textContent = <>{formatMessage(messages.errorUnknown)}</>;
	// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
	return isVisualRefreshEnabled() ? <Text as="p">{textContent}</Text> : <p>{textContent}</p>;
};

export const CmdbFieldError = (props: Props) => {
	const errorBody = useMemo(() => {
		const fireErrorAnalyticsWithErrorDescription = (
			errorDescription: string,
			isErrorUnknown: boolean,
		) => {
			const error = new Error(errorDescription);
			// Only report to Sentry cases where error is unknown
			// @ts-expect-error skipSentry is a custom property
			error.skipSentry = !isErrorUnknown;
			fireCmdbErrorAnalytics({
				error,
				meta: {
					id: 'renderErrorMessage',
					packageName: 'jiraIssueFieldCmdbObject',
					teamName: 'falcons',
				},
				attributes: {
					isCmdbEnabled: props.isCmdbEnabled,
					hasPremium: props.hasPremium,
					hasSchema: props.hasSchema,
					hasInvalidWorkspaceId: props.hasInvalidWorkspaceId,
					isAdmin: props.isAdmin,
					isObjectListUnenriched: props.isObjectListUnenriched,
					isErrorUnknown,
				},
			});
		};

		// CMDB is not enabled in the current environment (FedRamp)
		if (!props.isCmdbEnabled) {
			fireErrorAnalyticsWithErrorDescription('Insight field rendered an error', false);
			return <NotEnabledMessage />;
		}

		// Not premium
		if (!props.hasPremium && !props.isAdmin) {
			fireErrorAnalyticsWithErrorDescription('Insight field rendered an error', false);
			return <NotPremiumMessage />;
		}

		// Not premium - admin
		if (!props.hasPremium && props.isAdmin) {
			fireErrorAnalyticsWithErrorDescription('Insight field rendered an error', false);
			return <NotPremiumAdminMessage />;
		}

		// Invalid schema
		if (props.hasPremium && !props.hasSchema && !props.isAdmin) {
			fireErrorAnalyticsWithErrorDescription('Insight field rendered an error', false);
			return <InvalidSchemaMessage />;
		}

		// Invalid schema - admin
		if (props.hasPremium && !props.hasSchema && props.isAdmin) {
			fireErrorAnalyticsWithErrorDescription('Insight field rendered an error', false);
			return <InvalidSchemaAdminMessage fieldId={props.fieldId} />;
		}

		// Unenriched object list
		if (props.isObjectListUnenriched) {
			fireErrorAnalyticsWithErrorDescription('Insight field rendered an error', false);
			return <ObjectListUnenriched />;
		}

		// If we ever land here, it is because of a logic error and should be
		// fixed. Logging an error event so we can catch potential cases that
		// land here with our monitoring.
		fireErrorAnalyticsWithErrorDescription('Insight field rendered an unexpected error', true);

		// Fallback error
		return <FallbackError />;
	}, [
		props.isCmdbEnabled,
		props.hasPremium,
		props.isAdmin,
		props.hasSchema,
		props.isObjectListUnenriched,
		props.hasInvalidWorkspaceId,
		props.fieldId,
	]);

	return (
		<InlineMessage
			appearance="error"
			placement="bottom-end"
			testId="issue-field-cmdb-object.ui.error.inline-message"
		>
			<InlineMessageContent>{errorBody}</InlineMessageContent>
		</InlineMessage>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.span({
	marginRight: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InlineMessageContentWrapper = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& a, & span': {
		padding: '0 0 0 0',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& div': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		maxWidth: `${gridSize * 39}px`,
	},
});
